import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import FormRow from '@/components/core/form/FormRow';
import { useNodeById, useNodes } from '@/stores/analysisStore';
import { useEffect } from 'react';
import { getEmissionTypes } from '@/utils/pathwayUtils';
import debounce from 'debounce';
import Select from '@/components/core/form/Select';
import { FormProvider } from 'react-hook-form';
import LevelizationForm from '@/components/core/LevelizationForm';
import { ModuleSelect } from '@/components/core/form/ModuleSelect';
import { getYearOptions } from '@/utils/analysisUtils';

const levelizeOptions = [
  { value: false, label: 'Total' },
  { value: true, label: 'Per Functional Unit' },
];

const LCAPlotForm = ({ defaultValues, onSubmit, showYear = true, showScope3Breakdown = true }) => {
  const methods = useForm({ defaultValues });
  const { handleSubmit, watch, register, control, setValue } = methods;
  const nodes = useNodes();
  const yearOptions = getYearOptions(defaultValues.start_year, defaultValues.end_year);

  const [nodeId, levelize, levelizeBy, levelizeUnit, levelizableIos] = useWatch({
    control,
    name: ['nodeId', 'levelize', 'levelizeBy', 'levelizeUnit', 'levelizable_ios'],
    defaultValue: defaultValues,
  });

  const node = useNodeById(nodeId);
  const hasInternals = !!node?.internalPathwayId;

  const debouncedSave = debounce(() => handleSubmit(onSubmit)(), 10);
  const emissionTypeOptions = getEmissionTypes(nodes);
  const defaultEmissionType = emissionTypeOptions?.[0]?.value;

  // when node is changed update default emissionType
  useEffect(() => {
    setValue('emissionType', defaultEmissionType);
    setValue('year', '');
  }, [nodeId, defaultEmissionType, setValue]);

  // submit form on every input change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/incompatible-library
    const subscription = watch(debouncedSave);
    return () => subscription.unsubscribe();
  }, [watch, debouncedSave]);

  return (
    <div className="group node">
      <FormProvider {...methods}>
        <form>
          <div className="mt-2 grid grid-cols-3 group-[.emissions]:grid-cols-5 gap-2">
            <div>
              <ModuleSelect />
            </div>
            <div>
              <FormRow label="Values" name="levelize">
                <Select className="w-full truncate" name="levelize" options={levelizeOptions} />
              </FormRow>
            </div>
            <div>
              <FormRow label="Y axis" name="emissionType">
                <Select className="w-full truncate" name="emissionType" options={emissionTypeOptions} />
              </FormRow>
            </div>
            {hasInternals && (
              <div>
                <FormRow label="Add Scope 1 breakdown" name="scope1Breakdown">
                  <input
                    {...register('scope1Breakdown')}
                    id="scope1Breakdown"
                    name="scope1Breakdown"
                    type="checkbox"
                    className="toggle toggle-sm toggle-success mt-1 w-full truncate"
                  />
                </FormRow>
              </div>
            )}
            {showScope3Breakdown && (
              <div>
                <FormRow label="Add Scope 3 breakdown" name="scope3Breakdown">
                  <input
                    {...register('scope3Breakdown')}
                    id="scope3Breakdown"
                    name="scope3Breakdown"
                    type="checkbox"
                    className="toggle toggle-sm toggle-success mt-1 w-full truncate"
                  />
                </FormRow>
              </div>
            )}
            <LevelizationForm
              node={node}
              levelizableIos={levelizableIos}
              levelizeBy={levelizeBy}
              levelizeUnit={levelizeUnit}
              levelize={levelize}
            />
            {showYear && (
              <div>
                <FormRow label="Year" name="year">
                  <Select
                    className="w-full truncate"
                    name="year"
                    options={yearOptions}
                    registerOptions={{ valueAsNumber: true }}
                  />
                </FormRow>
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

LCAPlotForm.propTypes = {
  params: PropTypes.arrayOf(PropTypes.object),
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  showYear: PropTypes.bool,
  showScope3Breakdown: PropTypes.bool,
};

export default LCAPlotForm;
